import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { withShareUtm } from '@goaltree/backend/convex/lib/shareUtm'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@goaltree/ui/components/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@goaltree/ui/components/select'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import { Check, Copy, Globe, Lock, Trophy } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'
import { patchGoal } from '@/lib/goals-optimistic'

type Access = 'restricted' | 'view' | 'edit'

// Notion-style share modal: one place for general access (link + view/edit
// permission) and, separately, public profile/leaderboard listing.
export function ShareGoalDialog({
  goalId,
  goalSlug,
  isReadOnly,
  isPublic,
  open,
  onOpenChange,
}: {
  goalId: Id<'nodes'>
  goalSlug: string
  isReadOnly?: boolean
  isPublic?: boolean
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation()
  const { isPro } = useIsPro()

  const share = useQuery(api.sharing.getShare, open ? { goalId } : 'skip')
  const createShare = useMutation(api.sharing.createShare)
  const revokeShare = useMutation(api.sharing.revokeShare).withOptimisticUpdate(
    (store, { goalId }) => store.setQuery(api.sharing.getShare, { goalId }, null),
  )
  const setReadOnly = useMutation(api.nodes.setReadOnly).withOptimisticUpdate(
    (store, { goalId, isReadOnly }) => patchGoal(store, goalId, { isReadOnly }),
  )
  const setIsPublic = useMutation(api.nodes.setIsPublic).withOptimisticUpdate(
    (store, { goalId, isPublic }) => patchGoal(store, goalId, { isPublic }),
  )

  const [copied, setCopied] = useState(false)
  const [pending, setPending] = useState(false)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [confirmRestrict, setConfirmRestrict] = useState(false)
  const [localPublic, setLocalPublic] = useState(isPublic !== false)

  // Live-derived: a link exists (getShare) and its permission (isReadOnly).
  const linkOn = !!share
  const access: Access = !linkOn ? 'restricted' : isReadOnly === false ? 'edit' : 'view'

  const shareUrl =
    access === 'edit'
      ? `https://goaltree.ai/share/${goalSlug}?edit=true`
      : `https://goaltree.ai/share/${goalSlug}`

  async function applyAccess(next: Access) {
    if (next === access) return
    if (next === 'edit' && !isPro) {
      setUpgradeOpen(true)
      return
    }
    setPending(true)
    try {
      if (next === 'restricted') {
        await Promise.all([revokeShare({ goalId }), setReadOnly({ goalId, isReadOnly: true })])
      } else if (next === 'view') {
        await Promise.all([createShare({ goalId }), setReadOnly({ goalId, isReadOnly: true })])
      } else {
        await Promise.all([createShare({ goalId }), setReadOnly({ goalId, isReadOnly: false })])
      }
    } catch {
      toast.error(t('shareModal.updateError'))
    } finally {
      setPending(false)
    }
  }

  async function copyLink() {
    await navigator.clipboard.writeText(withShareUtm(shareUrl, 'goal_link'))
    setCopied(true)
    toast.success(t('common.shareLinkCopied'))
    setTimeout(() => setCopied(false), 2000)
  }

  async function toggleListed() {
    const next = !localPublic
    setLocalPublic(next)
    try {
      await setIsPublic({ goalId, isPublic: next })
    } catch {
      setLocalPublic(!next)
      toast.error(t('common.visibilityUpdateError'))
    }
  }

  return (
    <>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="edit-goal" />

      <AlertDialog open={confirmRestrict} onOpenChange={setConfirmRestrict}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('shareModal.restrictTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('shareModal.restrictDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmRestrict(false)
                void applyAccess('restricted')
              }}
            >
              {t('shareModal.restrictConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('shareModal.title')}</DialogTitle>
          </DialogHeader>

          {/* General access — who can open the link, and what they can do. */}
          <div className="mt-2 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('shareModal.generalAccess')}
            </p>
            <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                {linkOn ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <Select
                  value={linkOn ? 'link' : 'restricted'}
                  onValueChange={(v) => {
                    if (v === 'restricted') setConfirmRestrict(true)
                    else void applyAccess(access === 'edit' ? 'edit' : 'view')
                  }}
                  disabled={pending}
                >
                  <SelectTrigger className="h-auto w-auto border-0 bg-transparent p-0 text-sm font-medium shadow-none focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="restricted">{t('shareModal.restricted')}</SelectItem>
                    <SelectItem value="link">{t('shareModal.anyoneWithLink')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {linkOn ? t('shareModal.anyoneWithLinkDesc') : t('shareModal.restrictedDesc')}
                </p>
              </div>
              {linkOn && (
                <Select
                  value={access === 'edit' ? 'edit' : 'view'}
                  onValueChange={(v) => void applyAccess(v as Access)}
                  disabled={pending}
                >
                  <SelectTrigger className="w-auto shrink-0 rounded-md border bg-background text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="view">{t('shareModal.canView')}</SelectItem>
                    <SelectItem value="edit">{t('shareModal.canEdit')}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {linkOn && (
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1 truncate rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                  {shareUrl}
                </div>
                <button
                  type="button"
                  onClick={copyLink}
                  className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {t('shareModal.copyLink')}
                </button>
              </div>
            )}
          </div>

          {/* Public listing — independent of the share link. */}
          <div className="mt-5 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('shareModal.publicListing')}
            </p>
            <button
              type="button"
              onClick={() => void toggleListed()}
              className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5 text-left"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                <Trophy className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{t('shareModal.showOnProfile')}</p>
                <p className="text-xs text-muted-foreground">{t('shareModal.showOnProfileDesc')}</p>
              </div>
              <span
                className={cn(
                  'relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors',
                  localPublic ? 'bg-primary' : 'bg-muted-foreground/25',
                )}
              >
                <span
                  className={cn(
                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
                    localPublic ? 'translate-x-5' : 'translate-x-0',
                  )}
                />
              </span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
